/**
 * UsedInPanel — "one project, many contexts"
 *
 * Place at: src/components/overlays/UsedInPanel.jsx
 * Mounted by: ProjectDetailOverlay.jsx (between Creative Lineage and collections)
 *
 * A Use says: this exact work is being used HERE, for THIS reason.
 *   Stencil -> Lighting: "Cut as a GOBO for the gallery wash"
 *   Stencil -> Ceramics: "Pressed into leather-hard clay"
 * One file, one version history. Update it and every Use sees the update,
 * unless that Use is pinned to a version (a class grading v2 while v3 ships).
 *
 * Same file, new purpose -> Use.   Changed file -> Fork.
 *
 * OWNER-ONLY: only the project owner can add a Use (enforced server-side in
 * uses_api.create_project_use). Everyone else connects to a project by
 * forking it, pinning it to a collection, or mentioning it (backlinks).
 * Non-owners only see this panel when the project already has Uses.
 *
 * Permissions for existing Uses come from the server (use.permissions) --
 * the panel never re-derives who may hide/edit/delete, so it cannot drift
 * from the API.
 *
 * FAILS QUIET: if the endpoint 404s (feature flag off, or the project is not
 * visible) the panel renders nothing. Safe to deploy before the migration.
 *
 * Zone colors are inline styles, NOT runtime Tailwind classes -- a class
 * built as `bg-zone-${id}` would be purged from the production build
 * (see the NO SAFELIST note in tailwind.config.js).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers, Plus, EyeOff, Eye, Trash2, MessageCircle, Pin, Sparkles,
  GraduationCap, CornerDownRight, X, Send,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { Avatar } from '../ui';
import { ZONES } from '../../config/zones';
import { useAuth } from '../../context/AuthContext';
import { usesAPI, api } from '../../api';

const NOTE_MAX = 280;
const COMMENT_MAX = 2000;
const FALLBACK_ZONE = { id: 'default', color: '#71717a', shortLabel: '?', label: 'Unknown' };

const zoneOf = (id) => ZONES[id] || FALLBACK_ZONE;

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  if (s < 2592000) return `${Math.floor(s / 604800)}w ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
};

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

/** Escape inside a field closes the field, not the whole overlay. */
const stopEscape = (onEscape) => (e) => {
  if (e.key === 'Escape') {
    e.stopPropagation();
    onEscape?.();
  }
};


// ============================================================================
// ZONE CHIP
// ============================================================================

const ZoneChip = ({ zoneId, size = 'sm' }) => {
  const z = zoneOf(zoneId);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-semibold flex-shrink-0',
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
      )}
      style={{ backgroundColor: `${z.color}18`, color: z.color, border: `1px solid ${z.color}35` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: z.color }} />
      {z.shortLabel}
    </span>
  );
};


// ============================================================================
// THREAD
// ============================================================================

const UseThread = ({ use, onCountChange }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    usesAPI.getComments(use.id)
      .then((res) => { if (!cancelled) setComments(res.data.comments || []); })
      .catch(() => { if (!cancelled) setComments([]); });
    return () => { cancelled = true; };
  }, [use.id]);

  // One level of nesting: replies to replies attach to the root comment.
  // Deep threads are unreadable inside a 32rem overlay.
  const threaded = useMemo(() => {
    if (!comments) return [];
    const byId = new Map(comments.map((c) => [c.id, c]));
    const rootOf = (c) => {
      let cur = c;
      const seen = new Set();
      while (cur.parentCommentId && byId.has(cur.parentCommentId) && !seen.has(cur.id)) {
        seen.add(cur.id);
        cur = byId.get(cur.parentCommentId);
      }
      return cur;
    };
    const roots = [];
    const children = new Map();
    for (const c of comments) {
      const root = rootOf(c);
      if (root.id === c.id) roots.push(c);
      else {
        if (!children.has(root.id)) children.set(root.id, []);
        children.get(root.id).push(c);
      }
    }
    return roots.map((r) => ({ root: r, replies: children.get(r.id) || [] }));
  }, [comments]);

  const liveCount = (list) => list.filter((c) => !c.isDeleted).length;

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await usesAPI.addComment(use.id, content, replyTo?.id ?? null);
      const next = [...(comments || []), res.data.comment];
      setComments(next);
      onCountChange?.(liveCount(next));
      setDraft('');
      setReplyTo(null);
    } catch (err) {
      setError(errorText(err, 'Could not post that comment'));
    } finally {
      setSending(false);
    }
  };

  const remove = async (comment) => {
    try {
      const res = await usesAPI.deleteComment(comment.id);
      const next = comments.map((c) => (c.id === comment.id ? res.data.comment : c));
      setComments(next);
      onCountChange?.(liveCount(next));
    } catch (err) {
      setError(errorText(err, 'Could not delete that comment'));
    }
  };

  const CommentRow = ({ c, isReply }) => (
    <div className={cn('flex gap-2 group', isReply && 'ml-6')}>
      {isReply && <CornerDownRight className="w-3 h-3 text-zinc-700 mt-1 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        {c.isDeleted ? (
          <p className="text-[11px] text-zinc-600 italic">Comment removed</p>
        ) : (
          <>
            <p className="text-[11px] text-zinc-500">
              <span className="text-zinc-300 font-medium">@{c.author?.username}</span>
              <span className="ml-1.5 text-zinc-600">{timeAgo(c.createdAt)}</span>
            </p>
            <p className="text-[12px] text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">
              {c.content}
            </p>
            <div className="flex items-center gap-3 mt-0.5">
              {user && (
                <button
                  onClick={() => setReplyTo(c)}
                  className="text-[10px] text-zinc-600 hover:text-zinc-300 transition-colors"
                >
                  Reply
                </button>
              )}
              {c.permissions?.canDelete && (
                <button
                  onClick={() => remove(c)}
                  className="text-[10px] text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                >
                  Delete
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="mt-2.5 pt-2.5 border-t border-zinc-800/60 space-y-2.5">
      {comments === null && (
        <div className="flex items-center gap-2 text-[11px] text-zinc-600">
          <div className="w-3 h-3 border-2 border-zinc-700 border-t-purple-500 rounded-full animate-spin" />
          Loading thread...
        </div>
      )}

      {comments?.length === 0 && (
        <p className="text-[11px] text-zinc-600">
          No discussion yet. Questions about this use go here -- not on the project itself.
        </p>
      )}

      {threaded.map(({ root, replies }) => (
        <div key={root.id} className="space-y-1.5">
          <CommentRow c={root} />
          {replies.map((r) => <CommentRow key={r.id} c={r} isReply />)}
        </div>
      ))}

      {user && (
        <div className="space-y-1.5">
          {replyTo && (
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
              <CornerDownRight className="w-3 h-3" />
              Replying to @{replyTo.author?.username || 'comment'}
              <button onClick={() => setReplyTo(null)} className="text-zinc-600 hover:text-zinc-300">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, COMMENT_MAX))}
              onKeyDown={(e) => {
                stopEscape(() => { setDraft(''); setReplyTo(null); })(e);
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
              }}
              rows={1}
              placeholder="Ask about this use..."
              className="flex-1 min-h-[34px] max-h-28 px-3 py-2 text-[12px] rounded-lg resize-y
                         bg-zinc-900 border border-zinc-800 text-zinc-200 placeholder:text-zinc-600
                         focus:outline-none focus:border-zinc-600"
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending}
              aria-label="Post comment"
              className={cn(
                'h-[34px] w-[34px] flex items-center justify-center rounded-lg transition-all flex-shrink-0',
                draft.trim() && !sending
                  ? 'bg-purple-600 hover:bg-purple-500 text-white active:scale-95'
                  : 'bg-zinc-800 text-zinc-600',
              )}
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-[11px] text-red-400">{error}</p>}
    </div>
  );
};


// ============================================================================
// ONE USE
// ============================================================================

const UseRow = ({ use, isHomeZone, onChange, onRemove }) => {
  const [threadOpen, setThreadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const z = zoneOf(use.zoneId);

  const toggleHidden = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await usesAPI.setHidden(use.id, !use.isHidden);
      onChange(res.data.use);
    } catch (err) {
      setError(errorText(err, 'Could not change visibility'));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await usesAPI.remove(use.id);
      onRemove(use.id);
    } catch (err) {
      setError(errorText(err, 'Could not delete this use'));
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-xl p-3 border transition-all',
        use.isHidden
          ? 'bg-zinc-900/40 border-dashed border-zinc-800 opacity-60'
          : 'bg-zinc-800/25 border-zinc-800/50 hover:border-zinc-700/60',
      )}
    >
      {/* Zone accent rail */}
      <div
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ backgroundColor: use.isHidden ? '#3f3f46' : z.color }}
      />

      <div className="pl-2">
        {/* Top line: zone, class, version */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <ZoneChip zoneId={use.zoneId} />
          {!isHomeZone && !use.isHidden && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-fuchsia-400/80">
              <Sparkles className="w-2.5 h-2.5" />
              CROSS-ZONE
            </span>
          )}
          {use.cohort && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-800 text-zinc-400 border border-zinc-700/60 max-w-[10rem]">
              <GraduationCap className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="truncate">{use.cohort.name}</span>
            </span>
          )}
          {use.pinnedVersion && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/25"
              title="This context is frozen to one version. New uploads will not change it."
            >
              <Pin className="w-2.5 h-2.5" />
              v{use.pinnedVersion.versionNumber}
            </span>
          )}
          {use.isHidden && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-500 border border-zinc-700">
              <EyeOff className="w-2.5 h-2.5" />
              HIDDEN
            </span>
          )}
        </div>

        {/* The sentence */}
        <p className="text-[13px] text-zinc-200 leading-snug break-words">{use.note}</p>

        {/* Byline + actions */}
        <div className="flex items-center gap-2 mt-2">
          <Avatar src={use.creator?.profilePictureUrl} alt={use.creator?.username} size="xs" />
          <span className="text-[11px] text-zinc-500 truncate">
            @{use.creator?.username}
            <span className="text-zinc-700 mx-1">&bull;</span>
            {timeAgo(use.createdAt)}
          </span>

          <div className="flex items-center gap-0.5 ml-auto flex-shrink-0">
            {!use.isHidden && (
              <button
                onClick={() => setThreadOpen((v) => !v)}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded-md text-[11px] transition-colors',
                  threadOpen ? 'text-purple-300 bg-purple-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60',
                )}
                aria-expanded={threadOpen}
              >
                <MessageCircle className="w-3 h-3" />
                {use.commentCount > 0 ? use.commentCount : ''}
              </button>
            )}
            {use.permissions?.canHide && (
              <button
                onClick={toggleHidden}
                disabled={busy}
                title={use.isHidden ? 'Show this use again' : 'Hide this use from everyone else'}
                aria-label={use.isHidden ? 'Unhide use' : 'Hide use'}
                className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/60 transition-colors disabled:opacity-40"
              >
                {use.isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
            )}
            {use.permissions?.canDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                aria-label="Delete use"
                className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {confirmDelete && (
          <div className="mt-2.5 flex items-center justify-between gap-2 p-2 rounded-lg bg-red-500/[0.06] border border-red-500/20">
            <span className="text-[11px] text-red-300">Remove this use and its discussion?</span>
            <div className="flex gap-1.5">
              <button onClick={() => setConfirmDelete(false)} className="px-2.5 py-1 rounded-md text-[11px] text-zinc-400 hover:text-zinc-200">
                Cancel
              </button>
              <button onClick={doDelete} disabled={busy} className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50">
                Remove
              </button>
            </div>
          </div>
        )}

        {error && <p className="mt-1.5 text-[11px] text-red-400">{error}</p>}

        {threadOpen && !use.isHidden && (
          <UseThread
            use={use}
            onCountChange={(n) => onChange({ ...use, commentCount: n })}
          />
        )}
      </div>
    </div>
  );
};


// ============================================================================
// ADD-A-USE FORM  (owner only -- the panel never mounts it for anyone else)
// ============================================================================

const AddUseForm = ({ project, homeZoneId, latestVersion, onCreated, onCancel }) => {
  const [zoneId, setZoneId] = useState(null);
  const [note, setNote] = useState('');
  const [cohortId, setCohortId] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [pinVersion, setPinVersion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/cohorts')
      .then((res) => { if (!cancelled) setCohorts(res.data.cohorts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Home zone last: the interesting uses are the cross-zone ones.
  const zoneList = useMemo(() => {
    const all = Object.values(ZONES);
    return [...all.filter((z) => z.id !== homeZoneId), ...all.filter((z) => z.id === homeZoneId)];
  }, [homeZoneId]);

  const canSubmit = zoneId && note.trim() && !saving;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await usesAPI.create(project.id, {
        zoneId,
        note: note.trim(),
        cohortId: cohortId || null,
        pinnedVersionId: pinVersion && latestVersion?.id ? latestVersion.id : null,
      });
      onCreated(res.data.use, Boolean(res.data.alreadyExisted));
    } catch (err) {
      setError(errorText(err, 'Could not add this use'));
      setSaving(false);
    }
  };

  const remaining = NOTE_MAX - note.length;

  return (
    <form
      onSubmit={submit}
      onKeyDown={stopEscape(onCancel)}
      className="rounded-xl p-3.5 bg-zinc-800/30 border border-zinc-700/50 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200"
    >
      <div>
        <p className="text-[12px] font-semibold text-zinc-300">Where is this being used?</p>
        <p className="text-[11px] text-zinc-600 mt-0.5">
          Same file, new purpose. Changing the file? Fork it instead.
        </p>
      </div>

      {/* Zone picker */}
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Zone">
        {zoneList.map((z) => {
          const active = zoneId === z.id;
          return (
            <button
              key={z.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setZoneId(z.id)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all active:scale-95"
              style={active
                ? { backgroundColor: `${z.color}22`, color: z.color, borderColor: `${z.color}66` }
                : { backgroundColor: 'transparent', color: '#a1a1aa', borderColor: '#3f3f46' }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: z.color }} />
              {z.shortLabel}
              {z.id === homeZoneId && <span className="text-[9px] opacity-60">home</span>}
            </button>
          );
        })}
      </div>

      {/* The sentence */}
      <div>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
          placeholder="e.g. Cut as a GOBO for the gallery wash"
          maxLength={NOTE_MAX}
          autoFocus
          className="w-full px-3 py-2.5 text-[13px] rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-200
                     placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600"
        />
        <p className={cn('text-[10px] mt-1 text-right tabular-nums', remaining < 30 ? 'text-amber-400' : 'text-zinc-700')}>
          {remaining}
        </p>
      </div>

      {/* Class */}
      {cohorts.length > 0 && (
        <label className="block">
          <span className="text-[11px] text-zinc-500">For a class (optional)</span>
          <select
            value={cohortId}
            onChange={(e) => setCohortId(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-[12px] rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300
                       focus:outline-none focus:border-zinc-600"
          >
            <option value="">No class -- open use</option>
            {cohorts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.visibility === 'private' ? ' (private)' : c.visibility === 'campus' ? ' (campus)' : ''}
              </option>
            ))}
          </select>
          {cohorts.find((c) => String(c.id) === String(cohortId))?.visibility === 'private' && (
            <span className="block text-[10px] text-zinc-600 mt-1">Only members of this class will see this use.</span>
          )}
        </label>
      )}

      {/* Version pin */}
      {latestVersion?.id && (
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={pinVersion}
            onChange={(e) => setPinVersion(e.target.checked)}
            className="mt-0.5 accent-amber-500"
          />
          <span className="text-[11px] text-zinc-400 leading-snug">
            Pin to v{latestVersion.versionNumber || latestVersion.version_number}
            <span className="block text-zinc-600">
              {pinVersion
                ? 'This use stays on this version, even after new uploads.'
                : 'Off: this use always shows the latest version.'}
            </span>
          </span>
        </label>
      )}

      {error && <p className="text-[11px] text-red-400">{error}</p>}

      <div className="flex justify-end gap-2 pt-0.5">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 rounded-lg text-[12px] font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            'px-4 py-2 rounded-lg text-[12px] font-semibold transition-all',
            canSubmit ? 'text-white active:scale-[0.98]' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed',
          )}
          style={canSubmit ? { backgroundColor: zoneOf(zoneId).color } : undefined}
        >
          {saving ? 'Adding...' : 'Add use'}
        </button>
      </div>
    </form>
  );
};


// ============================================================================
// PANEL
// ============================================================================

export const UsedInPanel = ({ project, isOwner, homeZoneId, latestVersion }) => {
  const [uses, setUses] = useState(null);         // null = loading
  const [unavailable, setUnavailable] = useState(false);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState(null);

  const projectId = project?.id;

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await usesAPI.listForProject(projectId);
      setUses(res.data.uses || []);
      setUnavailable(false);
    } catch {
      // 404 = feature off or project not visible; anything else = stay quiet.
      setUnavailable(true);
    }
  }, [projectId]);

  useEffect(() => {
    setUses(null);
    setAdding(false);
    setNotice(null);
    load();
  }, [load]);

  const replaceUse = (next) => setUses((prev) => prev.map((u) => (u.id === next.id ? next : u)));
  const removeUse = (id) => setUses((prev) => prev.filter((u) => u.id !== id));

  const handleCreated = (use, alreadyExisted) => {
    setAdding(false);
    if (alreadyExisted) {
      setNotice('You already added a use in that zone -- here it is.');
    } else {
      setUses((prev) => [use, ...(prev || [])]);
      setNotice(null);
    }
  };

  if (unavailable) return null;

  // Non-owners never add Uses. While loading, or when there is nothing to
  // show them, render nothing -- no empty header, no spinner flash.
  if (!isOwner && (uses === null || uses.length === 0)) return null;

  const visibleUses = uses || [];
  const liveUses = visibleUses.filter((u) => !u.isHidden);
  const zonesUsed = [...new Set(liveUses.map((u) => u.zoneId))];
  const crossZoneCount = zonesUsed.filter((z) => z !== homeZoneId).length;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-[13px] font-semibold text-zinc-300 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-fuchsia-400" />
          Used in
          {liveUses.length > 0 && (
            <span className="text-[10px] text-zinc-600 font-normal">
              {liveUses.length} context{liveUses.length !== 1 ? 's' : ''}
              {crossZoneCount > 0 && (
                <span className="text-fuchsia-400/70 ml-1">
                  &middot; {crossZoneCount} other zone{crossZoneCount !== 1 ? 's' : ''}
                </span>
              )}
            </span>
          )}
        </h3>

        {/* Zone dot rail -- the at-a-glance "where does this live" */}
        {zonesUsed.length > 0 && (
          <div className="flex items-center gap-1" aria-hidden="true">
            {zonesUsed.map((zid) => (
              <span
                key={zid}
                className="w-2 h-2 rounded-full"
                title={zoneOf(zid).label}
                style={{ backgroundColor: zoneOf(zid).color }}
              />
            ))}
          </div>
        )}
      </div>

      {uses === null ? (
        <div className="flex items-center gap-2 text-[12px] text-zinc-600">
          <div className="w-3.5 h-3.5 border-2 border-zinc-700 border-t-fuchsia-500 rounded-full animate-spin" />
          Loading...
        </div>
      ) : (
        <div className="space-y-2">
          {visibleUses.length === 0 && !adding && (
            <p className="text-[12px] text-zinc-600 leading-relaxed">
              Using this in more than one class or discipline? Add each use -- one file, updated everywhere.
            </p>
          )}

          {visibleUses.map((use) => (
            <UseRow
              key={use.id}
              use={use}
              isHomeZone={use.zoneId === homeZoneId}
              onChange={replaceUse}
              onRemove={removeUse}
            />
          ))}

          {notice && <p className="text-[11px] text-zinc-500">{notice}</p>}

          {isOwner && (adding ? (
            <AddUseForm
              project={project}
              homeZoneId={homeZoneId}
              latestVersion={latestVersion}
              onCreated={handleCreated}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <button
              onClick={() => { setAdding(true); setNotice(null); }}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-medium
                         text-zinc-400 hover:text-zinc-200 border border-dashed border-zinc-800 hover:border-zinc-600
                         hover:bg-zinc-800/30 transition-all active:scale-[0.99]"
            >
              <Plus className="w-3.5 h-3.5" />
              Add a use
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UsedInPanel;